import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, NavLink, Outlet, Link } from "react-router-dom";
import { Home, Users, FolderOpen, Clapperboard, ClipboardList, Coins, Lock, Trophy, ChevronLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { DLLoading, StatusPill, Slogan, PlayBackdrop, SimulationDisclaimer } from "@/components/dl/ui";

const TABS = [
  { to: "", end: true, label: "Case HQ", icon: Home, testid: "dl-tab-hq" },
  { to: "team", label: "Team", icon: Users, testid: "dl-tab-team" },
  { to: "record", label: "Case Record", icon: FolderOpen, testid: "dl-tab-record" },
  { to: "film", label: "Film Room", icon: Clapperboard, testid: "dl-tab-film" },
  { to: "playbook", label: "Playbook", icon: ClipboardList, testid: "dl-tab-playbook" },
  { to: "resources", label: "Resources", icon: Coins, testid: "dl-tab-resources" },
  { to: "plays", label: "Locked Plays", icon: Lock, testid: "dl-tab-plays" },
  { to: "standings", label: "Standings", icon: Trophy, testid: "dl-tab-standings" },
];

export default function DLLayout() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [isCaptain, setIsCaptain] = useState(false);
  const [invites, setInvites] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, mine, ents, inv] = await Promise.all([
        api.get(`/dl/cases/${caseId}`),
        api.get(`/dl/cases/${caseId}/teams/mine`),
        api.get(`/dl/entitlements/me`),
        api.get(`/dl/cases/${caseId}/invites/mine`).catch(() => ({ data: [] })),
      ]);
      setCaseData(c.data);
      setTeam(mine.data.team || null);
      setRoster(mine.data.roster || []);
      setIsCaptain(!!mine.data.is_captain);
      setInvites(inv.data || []);
      setEntitlement((ents.data || []).find((e) => e.dl_case_id === caseId) || null);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Unable to load this Case.");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (<div className="min-h-screen bg-charcoal"><Navbar /><DLLoading label="Loading the case file…" /></div>);
  if (error || !caseData) return (
    <div className="min-h-screen bg-charcoal"><Navbar />
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="font-display text-2xl text-white mb-2">Case unavailable</div>
        <p className="text-white/50 mb-6">{error}</p>
        <Link to="/defense-league" className="text-gold hover:underline label-mono">← Back to Defense League</Link>
      </div>
    </div>
  );

  const entitled = entitlement && entitlement.effective_status === "active";
  const ctx = { caseId, caseData, team, roster, isCaptain, invites, entitlement, entitled, reload: load, navigate };

  return (
    <div className="min-h-screen bg-charcoal text-white">
      <Navbar />
      {/* Case header */}
      <div className="relative overflow-hidden border-b border-white/10 dl-chalk-strong">
        <PlayBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Link to="/defense-league" className="inline-flex items-center gap-1 text-white/40 hover:text-gold text-xs label-mono mb-3" data-testid="dl-back-link">
            <ChevronLeft className="w-3.5 h-3.5" /> Defense League
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            {caseData.number && <span className="font-display font-bold text-gold text-lg">CASE {caseData.number}</span>}
            <StatusPill status={caseData.status} />
            {team && <span className="inline-flex items-center gap-1.5 text-xs font-mono text-white/70 border border-white/15 rounded-sm px-2 py-0.5"><Users className="w-3 h-3" /> {team.team_name}</span>}
          </div>
          <h1 className="mt-2 font-display font-bold text-2xl sm:text-4xl tracking-tight" data-testid="dl-case-title">{caseData.title}</h1>
          <Slogan className="block mt-2">Watch the film. Study the record. Call your play.</Slogan>
        </div>
      </div>
      {/* Sub-navigation */}
      <div className="sticky top-16 z-40 border-b border-white/10 bg-charcoal/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-2 sm:px-6">
          <div className="dl-subnav flex gap-1 overflow-x-auto py-2">
            {TABS.map((t) => (
              <NavLink key={t.label} to={t.to} end={t.end} data-testid={t.testid}
                className={({ isActive }) => `shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-sm text-sm whitespace-nowrap transition-colors ${isActive ? "bg-gold text-charcoal font-semibold" : "text-white/65 hover:text-gold hover:bg-white/5"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <SimulationDisclaimer compact className="mb-5" />
        <Outlet context={ctx} />
      </main>
    </div>
  );
}
